import { useSyncExternalStore } from 'react'
import type { CommentedBlock } from './commented-blocks.js'

/**
 * What the open song tells the sidebar Chat (chat makeover M3, #2718, plan D2): its commented blocks, and a way to
 * show one, which scrolls to it and opens its comments. The sidebar has no other handle on the open editor.
 */
export interface OpenDocument {
  blocks: CommentedBlock[]
  showInSong(blockId: string): void
}

const documents = new Map<string, OpenDocument>()
const listeners = new Set<() => void>()

const sameBlocks = (a: CommentedBlock[], b: CommentedBlock[]) =>
  a.length === b.length &&
  a.every(
    (block, index) =>
      block.id === b[index]?.id &&
      block.label === b[index]?.label &&
      block.quoted === b[index]?.quoted &&
      block.sectionType === b[index]?.sectionType
  )

export const subscribeOpenDocuments = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const notify = () => listeners.forEach(listener => listener())

export const getOpenDocument = (entityId: string | undefined) => (entityId ? documents.get(entityId) : undefined)

/**
 * Publish an open document's commented blocks. The same blocks again keep the stored object, so readers re-render
 * only when a block, its label or its section changes, not per keystroke. Returns the unpublish function, which
 * removes the document only while it is still the one this call published.
 */
export const publishOpenDocument = (entityId: string, document: OpenDocument) => {
  const current = documents.get(entityId)
  const stored =
    current && current.showInSong === document.showInSong && sameBlocks(current.blocks, document.blocks)
      ? current
      : document
  if (stored !== current) {
    documents.set(entityId, stored)
    notify()
  }
  return () => {
    if (documents.get(entityId) === stored) {
      documents.delete(entityId)
      notify()
    }
  }
}

/** The open document for an entity, or undefined while no editor has it open */
export const useOpenDocument = (entityId: string | undefined) =>
  useSyncExternalStore(subscribeOpenDocuments, () => getOpenDocument(entityId))
